import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { InputDropdownOption } from '@interxfi/design-system/components/input/shared/dropdown/input-dropdown';
import { SwitchInputGroup } from '@interxfi/design-system/components/input/switch-input/group/switch-input-group';
import { IxcRegistrationV1ExemptPayeeCode, type IxcRegistrationV1OrganizationW9 } from '@interxfi/types';
import { parseNumber } from '@interxfi/utilities/number/parse';
import { AppDatePickerInputWithLens } from '../../../../../components/form/app-date-picker-input';
import { AppEnumSelectInputWithLens } from '../../../../../components/form/app-enum-select-input';
import { AppSwitchInputWithLens } from '../../../../../components/form/app-switch-input';
import { addDefinedValues } from '../../../../../util/has-value';
import { W9_BACKUP_WITHHOLDING_TOOLTIP, W9_CERTIFICATION_TOOLTIP, W9_EXEMPT_PAYEE_TOOLTIP, W9_FOREIGN_OWNERS_TOOLTIP } from '../../tooltips';

export const organizationProfileW9FormSchema = z.object({
  isSubjectToBackupWithholding: z.boolean(),
  foreignOwners: z.boolean(),
  exemptPayeeCode: z.enum(IxcRegistrationV1ExemptPayeeCode).or(z.literal('')),
  certification: z.literal(true, { error: 'Certification is required' }),
  signed: z.iso.date().nonempty('Signature date is required'),
});

export type OrganizationProfileW9FormValues = z.infer<typeof organizationProfileW9FormSchema>;

export const organizationProfileW9FormDefaultValues: OrganizationProfileW9FormValues = {
  isSubjectToBackupWithholding: false,
  foreignOwners: false,
  exemptPayeeCode: '',
  certification: false as true, // weird, but has to be true to pass validation. It will be set by the user.
  signed: '',
};

export function getInitialFormValuesForOrganizationW9(data: IxcRegistrationV1OrganizationW9 | undefined): OrganizationProfileW9FormValues {
  return {
    ...organizationProfileW9FormDefaultValues,
    foreignOwners: data?.foreignOwners ?? organizationProfileW9FormDefaultValues.foreignOwners,
    exemptPayeeCode: data?.exemptPayeeCode ?? organizationProfileW9FormDefaultValues.exemptPayeeCode,
    isSubjectToBackupWithholding: data?.isSubjectToBackupWithholding ?? organizationProfileW9FormDefaultValues.isSubjectToBackupWithholding,
    certification: (data?.certification ?? organizationProfileW9FormDefaultValues.certification) as true, // once again, weird but required to satisfy the schema
    signed: data?.signed ?? '',
  };
}

export function formValuesToOrganizationW9(values: OrganizationProfileW9FormValues): IxcRegistrationV1OrganizationW9 {
  return addDefinedValues<IxcRegistrationV1OrganizationW9>(
    {
      foreignOwners: values.foreignOwners,
      isSubjectToBackupWithholding: values.isSubjectToBackupWithholding,
      certification: values.certification,
      signed: values.signed,
    },
    {
      exemptPayeeCode: values.exemptPayeeCode as IxcRegistrationV1ExemptPayeeCode,
    },
  );
}

const descriptionsByExemptPayeeCode: Record<IxcRegistrationV1ExemptPayeeCode, string> = {
  [IxcRegistrationV1ExemptPayeeCode.Unspecified]: 'Not specified', // This should not be selectable by the user
  [IxcRegistrationV1ExemptPayeeCode['1ExemptOrg']]:
    'An organization exempt from tax under section 501(a), any IRA, or a custodial account under section 403(b)(7) if the account satisfies the requirements of section 401(f)(2).',
  [IxcRegistrationV1ExemptPayeeCode['2UsGvt']]: 'The United States or any of its agencies or instrumentalities',
  [IxcRegistrationV1ExemptPayeeCode['3StateGvt']]:
    'A state, the District of Columbia, a U.S. commonwealth or territory, or any of their political subdivisions or instrumentalities.',
  [IxcRegistrationV1ExemptPayeeCode['4FrgnGvt']]: 'A foreign government or any of its political subdivisions, agencies, or instrumentalities.',
  [IxcRegistrationV1ExemptPayeeCode['5Corp']]: 'A corporation.',
  [IxcRegistrationV1ExemptPayeeCode['6Dealer']]:
    'A dealer in securities or commodities required to register in the United States, the District of Columbia, or a U.S. commonwealth or territory.',
  [IxcRegistrationV1ExemptPayeeCode['7Fcm']]: 'A futures commission merchant registered with the Commodity Futures Trading Commission.',
  [IxcRegistrationV1ExemptPayeeCode['8Reit']]: 'A real estate investment trust.',
  [IxcRegistrationV1ExemptPayeeCode['9InvmtCo']]: 'An entity registered at all times during the tax year under the Investment Company Act of 1940.',
  [IxcRegistrationV1ExemptPayeeCode['10CommonTr']]: 'A common trust fund operated by a bank under section 584(a).',
  [IxcRegistrationV1ExemptPayeeCode['11FinclInst']]: 'A financial institution.',
  [IxcRegistrationV1ExemptPayeeCode['12Nominee']]: 'A middleman known in the investment community as a nominee or custodian.',
  [IxcRegistrationV1ExemptPayeeCode['13ExemptTr']]: 'A trust exempt from tax under section 664 or described in section 4947.',
};

function exemptPayeeCodeSelectOptionModifier(
  options: InputDropdownOption<IxcRegistrationV1ExemptPayeeCode>[],
): InputDropdownOption<IxcRegistrationV1ExemptPayeeCode>[] {
  return options
    .map((option) => ({
      ...option,
      description: descriptionsByExemptPayeeCode[option.value as IxcRegistrationV1ExemptPayeeCode] || undefined,
    }))
    .sort((a, b) => {
      const aAsNumber = parseInt(parseNumber(a.value), 10);
      const bAsNumber = parseInt(parseNumber(b.value), 10);

      return aAsNumber - bAsNumber;
    });
}

interface OrganizationProfileW9FormProps {
  lens: Lens<OrganizationProfileW9FormValues>;
}

export function OrganizationProfileW9Form({ lens }: OrganizationProfileW9FormProps) {
  return (
    <VerticalStack gap={4}>
      <SwitchInputGroup>
        <AppSwitchInputWithLens
          info={W9_BACKUP_WITHHOLDING_TOOLTIP}
          label="Subject to Backup Withholding"
          lens={lens.focus('isSubjectToBackupWithholding').defined()}
        />
        <AppSwitchInputWithLens info={W9_CERTIFICATION_TOOLTIP} label="Certification" lens={lens.focus('certification')} required />
        <AppSwitchInputWithLens info={W9_FOREIGN_OWNERS_TOOLTIP} label="Foreign Owners" lens={lens.focus('foreignOwners')} />
      </SwitchInputGroup>

      <AppEnumSelectInputWithLens
        enumName="IxcRegistrationV1ExemptPayeeCode"
        enumValue={IxcRegistrationV1ExemptPayeeCode}
        i18nNamespace="ixcRegistrationV1"
        info={W9_EXEMPT_PAYEE_TOOLTIP}
        label="Exempt Payee Code"
        lens={lens.focus('exemptPayeeCode').narrow<IxcRegistrationV1ExemptPayeeCode>()}
        optionModifier={exemptPayeeCodeSelectOptionModifier}
      />

      <AppDatePickerInputWithLens label="Signed" lens={lens.focus('signed')} required />
    </VerticalStack>
  );
}
